import { EyeIcon, EyeOffIcon } from "lucide-react";
import React, { forwardRef, InputHTMLAttributes, useId, useState } from "react";

type AuthInputProps = {
  label?: string;
  error?: string;
  helperText?: string;
} & InputHTMLAttributes<HTMLInputElement>;

const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(
  (
    {
      label,
      error,
      helperText,
      id,
      className = "",
      disabled = false,
      type = "text",
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === "password";

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

    return (
      <div className="space-y-2 w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="text-[#121217] text-sm font-medium"
          >
            {label}
          </label>
        )}

        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            type={isPassword ? (showPassword ? "text" : "password") : type}
            aria-invalid={!!error}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-help`
                  : undefined
            }
            className={`
              w-full
              rounded-xl
              px-4
              py-3
              text-sm                    /* 16px → prevents iOS zoom */
              leading-normal
              bg-white
              appearance-none              /* Removes iOS default styles */
              focus:outline-none
              focus:ring-[1.5px]
              focus:ring-[#FF6600]
              transition-all
              duration-200
              ${
                disabled
                  ? "bg-[#F2F4F7] cursor-not-allowed text-[#98A2B3]"
                  : "text-[#667085]"
              }
              ${
                error
                  ? "border border-red-500 focus:ring-red-500"
                  : "border border-[#E4E7EC]"
              }
              ${isPassword ? "pr-12" : ""}
              ${className}
            `}
            {...props}
          />

          {isPassword && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="
                absolute 
                right-3 
                top-1/2 
                -translate-y-1/2
                p-2
                min-w-[40px] 
                min-h-[40px]     /* Better touch target */
                flex 
                items-center 
                justify-center
                text-gray-400 
                active:scale-95
                transition
              "
            >
              {showPassword ? (
                <EyeOffIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          )}
        </div>

        {helperText && !error && (
          <p id={`${inputId}-help`} className="text-xs text-[#667085]">
            {helperText}
          </p>
        )}

        {error && (
          <p id={`${inputId}-error`} className="text-xs text-red-500">
            {error}
          </p>
        )}
      </div>
    );
  },
);

AuthInput.displayName = "AuthInput";

export default AuthInput;
